import { mightFail } from 'might-fail'
import { z } from 'zod'
import { arrayBufferToBase64 } from '../utils/array-buffer-to-base64'
import { fetchWithAuth } from '../auth/fetch-with-auth'

const CLOUD_API_URL = import.meta.env.VITE_CLOUD_API_URL
if (!CLOUD_API_URL) throw new Error('CLOUD_API_URL is not defined')

const FirmwareUpdateResponseSchema = z.object({
  url: z.string().url()
})

/** Fetches the firmware binary for a specific machine and returns it as base64. */
export const fetchFirmwareUpdate = async (machineId: string): Promise<string | null> => {
  // Step 1: Get the firmware download URL from the API
  const { error: networkError, result: response } = await fetchWithAuth(
    `${CLOUD_API_URL}/firmware-update?machineId=${encodeURIComponent(machineId)}`,
    { method: 'GET', timeout: 10_000 }
  )

  if (networkError) {
    console.error('fetchFirmwareUpdate network error', networkError.message)
    return null
  }

  const { error: parseError, result: parsed } = await mightFail(
    response.json().then((json: unknown) => FirmwareUpdateResponseSchema.parse(json))
  )

  if (parseError) {
    console.error('fetchFirmwareUpdate invalid response', parseError.message)
    return null
  }

  // Step 2: Fetch the actual firmware binary from the URL
  const { error: downloadError, result: firmwareResponse } = await mightFail(
    fetch(parsed.url, { signal: AbortSignal.timeout(120_000) })
  )

  if (downloadError) {
    console.error('fetchFirmwareUpdate download error', downloadError.message)
    return null
  }

  if (!firmwareResponse.ok) {
    console.error('fetchFirmwareUpdate download failed', firmwareResponse.status)
    return null
  }

  const { error: bufferError, result: buffer } = await mightFail(firmwareResponse.arrayBuffer())

  if (bufferError) {
    console.error('fetchFirmwareUpdate buffer error', bufferError.message)
    return null
  }

  return arrayBufferToBase64(buffer)
}
